/** One transport for JSON, multipart and downloads; every request binds its cabinet before awaiting auth. */
import { ApiException, type ApiError } from "./apiError";
import { supabase } from "./supabase";
import { beginTenantRequest, invalidateTenantScope } from "./tenantScope";

export { ApiException, type ApiError };
const API_URL =
  import.meta.env.VITE_API_BASE_URL ?? import.meta.env.VITE_API_URL ?? "http://localhost:3001";
async function authHeaders(): Promise<Record<string, string>> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    const result = await Promise.race([
      supabase.auth.getSession(),
      new Promise<{ data: { session: null } }>((resolve) => {
        timeout = setTimeout(() => resolve({ data: { session: null } }), 2000);
      }),
    ]);
    const token = result.data.session?.access_token;
    return token ? { authorization: `Bearer ${token}` } : {};
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}
async function request<T>(
  path: string,
  init: RequestInit & { noAuth?: boolean },
  consume: (response: Response) => Promise<T>,
): Promise<T> {
  const scope = beginTenantRequest(!["GET", "HEAD"].includes(init.method ?? "GET"));
  try {
    const headers = new Headers(init.headers);
    for (const [key, value] of Object.entries(scope.headers)) headers.set(key, value);
    if (!init.noAuth) {
      for (const [key, value] of Object.entries(await authHeaders())) headers.set(key, value);
    }
    scope.assertCurrent();
    if (init.signal?.aborted) throw new DOMException("Request aborted", "AbortError");
    const signal = scope.signal;
    const abort = () => scope.abort();
    init.signal?.addEventListener("abort", abort, { once: true });
    let response: Response;
    try {
      response = await fetch(`${API_URL}${path}`, { ...init, headers, signal });
    } finally {
      init.signal?.removeEventListener("abort", abort);
    }
    scope.assertCurrent();
    if (!response.ok) {
      const body = response.headers.get("content-type")?.includes("application/json")
        ? await response.json()
        : null;
      scope.assertCurrent();
      if (body?.error?.code === "AUTH_CABINET_ACCESS_UNAVAILABLE" && path !== "/v1/auth/me") {
        invalidateTenantScope();
      }
      throw new ApiException(
        response.status,
        body?.error ?? { code: "internal_error", message: `HTTP ${response.status}` },
      );
    }
    const data = await consume(response);
    scope.assertCurrent();
    return data;
  } finally {
    scope.finish();
  }
}
async function json<T>(response: Response): Promise<T> {
  const body = response.headers.get("content-type")?.includes("application/json")
    ? await response.json()
    : null;
  return (body?.data ?? body) as T;
}
export function apiFetch<T>(
  path: string,
  init: RequestInit & { json?: unknown; noAuth?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("content-type", "application/json");
  headers.set("accept", "application/json");
  return request(
    path,
    { ...init, headers, body: init.json !== undefined ? JSON.stringify(init.json) : init.body },
    json<T>,
  );
}
export async function apiDownload(path: string, filename: string): Promise<{ truncated: boolean }> {
  // Consume bytes before creating a browser download; a stale response never reaches the user.
  const { blob, truncated } = await request(path, {}, async (response) => ({
    blob: await response.blob(),
    truncated: response.headers.get("x-truncated") === "true",
  }));
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
  return { truncated };
}
export function apiPostMultipart<T>(path: string, formData: FormData): Promise<T> {
  return request(
    path,
    { method: "POST", headers: { accept: "application/json" }, body: formData },
    json<T>,
  );
}
/** Raw MIME shares the captured-cabinet transport, including stale-response refusal. */
export function apiPostEmail<T>(path: string, file: File): Promise<T> {
  return request(
    path,
    {
      method: "POST",
      headers: { "content-type": "message/rfc822", accept: "application/json" },
      body: file,
    },
    json<T>,
  );
}
type Options = { noAuth?: boolean };
export const api = {
  get: <T>(path: string, opts?: Options) => apiFetch<T>(path, opts),
  post: <T>(path: string, json: unknown, opts?: Options) =>
    apiFetch<T>(path, { method: "POST", json, ...opts }),
  patch: <T>(path: string, json: unknown, opts?: Options) =>
    apiFetch<T>(path, { method: "PATCH", json, ...opts }),
  put: <T>(path: string, json: unknown, opts?: Options) =>
    apiFetch<T>(path, { method: "PUT", json, ...opts }),
  del: <T>(path: string, opts?: Options) => apiFetch<T>(path, { method: "DELETE", ...opts }),
  postMultipart: apiPostMultipart,
  postEmail: apiPostEmail,
  download: apiDownload,
};
